import {
  DeactivateWorkerDeviceDtoPlatformEnum,
  GetWorkerDeviceStatusDtoPlatformEnum,
  UpdateWorkerDevicePreferenceDtoPlatformEnum,
  WorkerDeviceTokenDtoPlatformEnum,
} from '@workers-benefit-fund/api'
import type {
  DeactivateWorkerDeviceDto,
  GetWorkerDeviceStatusDto,
  UpdateWorkerDevicePreferenceDto,
  WorkerDeviceRegistrationStatusDTO,
  WorkerDeviceTokenDto,
} from '@workers-benefit-fund/api'

import { ENDPOINTS_TAGS, rootApi } from '@v2/store/api/rootApi'
import type { RootState } from '@v2/store/store'

interface WebPushRegistrationContext
  extends Omit<GetWorkerDeviceStatusDto, 'platform' | 'installationId'> {
  browserInstallationId: GetWorkerDeviceStatusDto['installationId']
  // Cache only. The backend derives membership from authentication.
  membershipId: string
}

type RegisterWebPushArgs = WebPushRegistrationContext &
  Pick<WorkerDeviceTokenDto, 'token' | 'notificationsEnabled'>

type UpdateWebPushPreferenceArgs = WebPushRegistrationContext &
  Pick<UpdateWorkerDevicePreferenceDto, 'notificationsEnabled'>

const registrationTag = ({
  membershipId,
  browserInstallationId,
}: WebPushRegistrationContext) => ({
  type: ENDPOINTS_TAGS.WEB_PUSH_REGISTRATION,
  id: JSON.stringify([membershipId, 'web', browserInstallationId]),
})

export const webPushApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getWebPushRegistrationStatus: builder.query<
      WorkerDeviceRegistrationStatusDTO,
      WebPushRegistrationContext
    >({
      query: ({ browserInstallationId }) => ({
        url: '/v2/push-notification/registration-status',
        method: 'GET',
        params: {
          platform: GetWorkerDeviceStatusDtoPlatformEnum.Web,
          installationId: browserInstallationId,
        } satisfies GetWorkerDeviceStatusDto,
      }),
      providesTags: (_result, _error, context) => [registrationTag(context)],
      keepUnusedDataFor: 0,
    }),
    registerWebPushDevice: builder.mutation<void, RegisterWebPushArgs>({
      async queryFn(
        { membershipId, browserInstallationId, token, notificationsEnabled },
        api,
        _options,
        baseQuery,
      ) {
        const canRegisterForMembership = () => {
          const state = api.getState() as RootState
          return (
            !api.signal.aborted &&
            state.auth.status === 'authenticated' &&
            state.auth.membershipId === membershipId &&
            state.organizationSwitch.status !== 'switching' &&
            !state.organizationSwitch.pendingEvent
          )
        }
        const cancelled = {
          error: {
            status: 'CUSTOM_ERROR',
            data: { code: 'NOTIFICATION_SESSION_CHANGED' },
          },
        }
        if (!canRegisterForMembership()) return cancelled
        const data = {
          platform: WorkerDeviceTokenDtoPlatformEnum.Web,
          installationId: browserInstallationId,
          token,
          ...(notificationsEnabled !== undefined && { notificationsEnabled }),
        } satisfies WorkerDeviceTokenDto

        const result = await baseQuery({
          url: '/v1/push-notification/web-push-token',
          method: 'PUT',
          data,
        })
        const error = result.error as
          | { status?: number; data?: { code?: string } }
          | undefined
        if (
          error?.status !== 409 ||
          error.data?.code !== 'PUSH_TOKEN_RENEWAL_REQUIRED' ||
          notificationsEnabled === false
        )
          return result.error ? { error: result.error } : { data: undefined }
        if (!canRegisterForMembership()) return cancelled
        try {
          const { renewWebPushToken } = await import('../utils/getWebPushToken')
          if (!canRegisterForMembership()) return cancelled
          data.token = await renewWebPushToken({
            rejectedToken: token,
            canRegisterForMembership,
          })
          if (!canRegisterForMembership()) return cancelled
          // One retry only. RTK stays pending throughout renewal and retry.
          const retry = await baseQuery({
            url: '/v1/push-notification/web-push-token',
            method: 'PUT',
            data,
          })
          return retry.error ? { error: retry.error } : { data: undefined }
        } catch {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: {
                code: 'PUSH_TOKEN_RENEWAL_FAILED',
                message:
                  'Could not renew the notification token. Please try again.',
              },
            },
          }
        }
      },
      invalidatesTags: (_result, error, context) =>
        error ? [] : [registrationTag(context)],
    }),
    deactivateWebPushDevice: builder.mutation<void, WebPushRegistrationContext>(
      {
        query: ({ browserInstallationId }) => ({
          url: '/v2/push-notification/deactivate',
          method: 'POST',
          data: {
            platform: DeactivateWorkerDeviceDtoPlatformEnum.Web,
            installationId: browserInstallationId,
          } satisfies DeactivateWorkerDeviceDto,
        }),
        invalidatesTags: (_result, error, context) =>
          error ? [] : [registrationTag(context)],
      },
    ),
    updateWebPushPreference: builder.mutation<
      void,
      UpdateWebPushPreferenceArgs
    >({
      query: ({ browserInstallationId, notificationsEnabled }) => ({
        url: '/v2/push-notification/preference',
        method: 'PATCH',
        data: {
          platform: UpdateWorkerDevicePreferenceDtoPlatformEnum.Web,
          installationId: browserInstallationId,
          notificationsEnabled,
        } satisfies UpdateWorkerDevicePreferenceDto,
      }),
      invalidatesTags: (_result, error, context) =>
        error ? [] : [registrationTag(context)],
    }),
  }),
})

export const {
  useGetWebPushRegistrationStatusQuery,
  useLazyGetWebPushRegistrationStatusQuery,
  useRegisterWebPushDeviceMutation,
  useDeactivateWebPushDeviceMutation,
  useUpdateWebPushPreferenceMutation,
} = webPushApi
